import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GlobalChatState,
  GlobalChatMessage,
  UseGlobalChatReturn,
  INITIAL_GLOBAL_CHAT_STATE,
  StartGlobalChatResponse,
  GetGlobalChatResponse,
  GlobalChatRespondResponse,
  EndGlobalChatResponse,
} from '../types/globalChat';

/**
 * Hook for managing global chat conversations.
 *
 * Provides functions to start, interact with, and manage
 * AI-powered global chat conversations.
 *
 * @example
 * ```tsx
 * const {
 *   state,
 *   isLoading,
 *   isSending,
 *   error,
 *   openChat,
 *   closeChat,
 *   toggleChat,
 *   startConversation,
 *   sendMessage,
 *   endConversation,
 * } = useGlobalChat();
 *
 * // Open the chat panel
 * openChat();
 *
 * // Start a new conversation
 * await startConversation();
 *
 * // Send a user message
 * await sendMessage("What properties are available in Austin?");
 * ```
 */
export function useGlobalChat(): UseGlobalChatReturn {
  const navigate = useNavigate();
  const [state, setState] = useState<GlobalChatState>(INITIAL_GLOBAL_CHAT_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hideFab, setHideFab] = useState(false);

  /**
   * Handle unauthorized responses by redirecting to login
   */
  const handleUnauthorizedResponse = useCallback((response: Response): boolean => {
    if (response.status === 401) {
      navigate('/login');
      return true;
    }
    return false;
  }, [navigate]);

  /**
   * Clear any error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Open the chat panel
   */
  const openChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: true }));
  }, []);

  /**
   * Close the chat panel
   */
  const closeChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: false }));
  }, []);

  /**
   * Toggle the chat panel open/closed
   */
  const toggleChat = useCallback(() => {
    setState(prev => ({ ...prev, isOpen: !prev.isOpen }));
  }, []);

  /**
   * Reset the chat to initial state
   */
  const resetChat = useCallback(() => {
    setState(INITIAL_GLOBAL_CHAT_STATE);
    setError(null);
  }, []);

  /**
   * Start a new conversation
   * @param initialMessage Optional initial message to send
   * @returns true if successful, false otherwise
   */
  const startConversation = useCallback(async (initialMessage?: string): Promise<boolean> => {
    setError(null);

    // If there's an initial message, optimistically add it immediately
    // and use isSending to show typing indicator
    let userMessage: GlobalChatMessage | null = null;
    if (initialMessage) {
      userMessage = {
        id: Date.now(),
        role: 'user',
        content: initialMessage.trim(),
        createdAt: new Date().toISOString(),
      };

      // Optimistically update state with user message and mark as active
      setState(prev => ({
        ...prev,
        isOpen: true,
        hasActiveConversation: true,
        messages: [userMessage!],
      }));
      setIsSending(true);
    } else {
      setIsLoading(true);
    }

    try {
      const body = initialMessage ? JSON.stringify({ initialMessage: initialMessage.trim() }) : undefined;

      const response = await fetch('/api/chat/global', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body,
      });

      if (handleUnauthorizedResponse(response)) {
        // Revert optimistic update on auth failure
        if (userMessage) {
          setState(prev => ({
            ...prev,
            hasActiveConversation: false,
            messages: [],
          }));
        }
        return false;
      }

      const data: StartGlobalChatResponse = await response.json();

      if (!data.success) {
        // Revert optimistic update on error
        if (userMessage) {
          setState(prev => ({
            ...prev,
            hasActiveConversation: false,
            messages: [],
          }));
        }
        setError(data.error || 'Failed to start conversation');
        return false;
      }

      // Update state with conversation details
      if (initialMessage && data.message) {
        // Add assistant response to existing user message
        const assistantMessage: GlobalChatMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.message,
          createdAt: new Date().toISOString(),
        };

        setState(prev => ({
          ...prev,
          conversationId: data.conversationId || null,
          conversationUuid: data.conversationUuid || null,
          messages: [...prev.messages, assistantMessage],
        }));
      } else {
        // No initial message, just set up the conversation
        setState(prev => ({
          ...prev,
          isOpen: true,
          hasActiveConversation: true,
          conversationId: data.conversationId || null,
          conversationUuid: data.conversationUuid || null,
          messages: [],
        }));
      }

      return true;
    } catch (err) {
      // Revert optimistic update on error
      if (userMessage) {
        setState(prev => ({
          ...prev,
          hasActiveConversation: false,
          messages: [],
        }));
      }
      const errorMessage = err instanceof Error ? err.message : 'Failed to start conversation';
      setError(errorMessage);
      console.error('Error starting global chat:', err);
      return false;
    } finally {
      setIsLoading(false);
      setIsSending(false);
    }
  }, [handleUnauthorizedResponse]);

  /**
   * Send a message to the current conversation
   * @param message The user's message content
   * @returns true if successful, false otherwise
   */
  const sendMessage = useCallback(async (message: string): Promise<boolean> => {
    if (!state.hasActiveConversation || !state.conversationId) {
      setError('No active conversation');
      return false;
    }

    if (!message.trim()) {
      setError('Message cannot be empty');
      return false;
    }

    setIsSending(true);
    setError(null);

    // Optimistically add user message to state
    const userMessage: GlobalChatMessage = {
      id: Date.now(),
      role: 'user',
      content: message.trim(),
      createdAt: new Date().toISOString(),
    };

    setState(prev => ({
      ...prev,
      messages: [...prev.messages, userMessage],
    }));

    try {
      const response = await fetch('/api/chat/global/respond', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          conversationId: state.conversationId,
          message: message.trim(),
        }),
      });

      if (handleUnauthorizedResponse(response)) {
        // Revert optimistic update on auth failure
        setState(prev => ({
          ...prev,
          messages: prev.messages.filter(m => m.id !== userMessage.id),
        }));
        return false;
      }

      const data: GlobalChatRespondResponse = await response.json();

      if (!data.success) {
        // Revert optimistic update on error
        setState(prev => ({
          ...prev,
          messages: prev.messages.filter(m => m.id !== userMessage.id),
        }));
        setError(data.error || 'Failed to send message');
        return false;
      }

      // Add the assistant's response message
      const assistantMessage: GlobalChatMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: data.message || '',
        createdAt: new Date().toISOString(),
      };

      setState(prev => ({
        ...prev,
        messages: [...prev.messages, assistantMessage],
      }));

      return true;
    } catch (err) {
      // Revert optimistic update on error
      setState(prev => ({
        ...prev,
        messages: prev.messages.filter(m => m.id !== userMessage.id),
      }));
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);
      console.error('Error sending message to global chat:', err);
      return false;
    } finally {
      setIsSending(false);
    }
  }, [state.hasActiveConversation, state.conversationId, handleUnauthorizedResponse]);

  /**
   * End the current conversation
   * @returns true if successful, false otherwise
   */
  const endConversation = useCallback(async (): Promise<boolean> => {
    if (!state.hasActiveConversation || !state.conversationId) {
      // Nothing to end, just reset state
      setState(prev => ({
        ...prev,
        hasActiveConversation: false,
        conversationId: null,
        conversationUuid: null,
        messages: [],
      }));
      return true;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/chat/global', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          conversationId: state.conversationId,
        }),
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: EndGlobalChatResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to end conversation');
        return false;
      }

      // Reset conversation state but keep chat open
      setState(prev => ({
        ...prev,
        hasActiveConversation: false,
        conversationId: null,
        conversationUuid: null,
        messages: [],
      }));

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to end conversation';
      setError(errorMessage);
      console.error('Error ending global chat:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [state.hasActiveConversation, state.conversationId, handleUnauthorizedResponse]);

  /**
   * Load previous conversations
   * @param limit Maximum number of conversations to load (default: 10)
   */
  const loadConversations = useCallback(async (limit?: number): Promise<void> => {
    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (limit) {
        params.set('limit', limit.toString());
      }

      const url = `/api/chat/global${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (handleUnauthorizedResponse(response)) {
        return;
      }

      const data: GetGlobalChatResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to load conversations');
        return;
      }

      setState(prev => ({
        ...prev,
        previousConversations: data.conversations || [],
      }));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversations';
      setError(errorMessage);
      console.error('Error loading global chat conversations:', err);
    } finally {
      setIsLoading(false);
    }
  }, [handleUnauthorizedResponse]);

  /**
   * Load a specific conversation by ID
   * @param conversationId The conversation ID to load
   * @returns true if successful, false otherwise
   */
  const loadConversation = useCallback(async (conversationId: number): Promise<boolean> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/chat/global?conversationId=${conversationId}`, {
        method: 'GET',
        credentials: 'same-origin',
      });

      if (handleUnauthorizedResponse(response)) {
        return false;
      }

      const data: GetGlobalChatResponse = await response.json();

      if (!data.success) {
        setError(data.error || 'Failed to load conversation');
        return false;
      }

      if (!data.conversation) {
        setError('Conversation not found');
        return false;
      }

      const isActive = data.conversation.status === 'active';

      setState(prev => ({
        ...prev,
        isOpen: true,
        hasActiveConversation: isActive,
        conversationId: data.conversation!.id,
        conversationUuid: data.conversation!.uuid,
        messages: data.conversation!.messages,
      }));

      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load conversation';
      setError(errorMessage);
      console.error('Error loading global chat conversation:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [handleUnauthorizedResponse]);

  return {
    state,
    isLoading,
    isSending,
    error,
    openChat,
    closeChat,
    toggleChat,
    startConversation,
    sendMessage,
    endConversation,
    loadConversations,
    loadConversation,
    clearError,
    resetChat,
    hideFab,
    setHideFab,
  };
}
